import type { UserGamePack } from "@skillscratch/shared-types";
import { useViewTransition } from "@/hooks/useViewTransition";
import { useStartGame } from "@/hooks/useStartGame";
import type { GameActionStartRet } from "@skillscratch/shared-types";
import { gameThemes } from "@/themes/themes";
import { Button } from "../ui/button";
import type { StartGameProps } from "../Modals/PurchaseModal";
import { useModal } from "@/hooks/useModal";
import { useEffect, useState } from "react";
import { ErrorModal } from "../Modals/ErrorModal";


export function UnplayedCardItem({ unplayedCard }: { unplayedCard: UserGamePack }) {
    const [startGameProps, setStartGameProps] = useState<StartGameProps | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const { navigateNormal } = useViewTransition(); 
    const { closeModalWithoutAnimation, openModal } = useModal();
    
    const { count } = unplayedCard;

    const { name, card: src, colorScheme } = gameThemes[unplayedCard.gameThemeId as keyof typeof gameThemes];
    const borderColor = colorScheme.controlColor;

    const { startGameHandler } = useStartGame({
        onSuccess: (startGameRet: GameActionStartRet, unplayedCard: UserGamePack) => {
            setStartGameProps({
                startGameRet,
                unplayedCard,
            });
            setIsLoading(false);
        },
        onError: () => {
            openModal(<ErrorModal message={"Error loading game"} />);
            setIsLoading(false);
        }
    });

    const onClickHandler = async () => {
        if (isLoading) {
            return;
        }
        
        if (!startGameProps) {
            return;
        }
        
        const { startGameRet, unplayedCard } = startGameProps;

        navigateNormal("/game", {
            replace: false,
            viewTransition: true,
            state: {
                gamePackId: unplayedCard.id,
                gameThemeId: unplayedCard.gameThemeId,
                cardCount: unplayedCard.count,
                startGameRet,
            },
        })
        closeModalWithoutAnimation();
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const initGame = async () => {
            setIsLoading(true);
            await startGameHandler(unplayedCard);
        };
        
        initGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // if (!isGameAvailable) {
    //     return null;
    // }

    return (
        <Button 
            variant={'bare'} 
            className="w-full h-full p-0 m-0 relative" 
            fillOnPress={true} 
            onClick={onClickHandler}
            disabled={isLoading}
        >
            <div className="flex items-center p-2 my-3 mx-3.5 border rounded-md border-blue-sky z-20 relative text-left">
                <div className="flex-shrink-0 mr-4">
                    <img src={src} alt="card-img" className={`h-20 rounded-lg border ${borderColor}`} 
                    />
                </div>
                <div className="flex-grow items-center flex-row">
                    <div>
                        <div className="text-black text-md opacity-40">{name}</div>
                        <div className="text-xl font-bold text-blue-dark">
                            {count} unplayed
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <Spinner />
                ) : (
                    /* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */
                    <span className={`ml-2 bg-blue-dark text-white rounded-full w-6 h-6 flex items-center justify-center`}>
                        {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </span>
                )}
            </div>
        </Button>
    );
}

function Spinner() {
    return (
        <span className="ml-2 text-blue-dark">
            {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
            <svg xmlns="http://www.w3.org/2000/svg" className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
        </span>
    );
}